// Descarga programática de archivos (PDF, respaldos de texto, etc.)
// compatible con navegadores web estándar y con la futura integración en Electron.
//
// Flujo seguro y a prueba de pestañas en blanco:
//  1. TODO el contenido (bytes del PDF o del texto) se resuelve ANTES de disparar
//     la descarga, de forma SÍNCRONA, en el mismo hilo del evento de clic, para no
//     perder el contexto del "gesto del usuario".
//  2. Se fuerza la descarga con 'application/octet-stream' en lugar de un MIME
//     renderizable (application/pdf, text/*), para que el navegador trate el archivo
//     como binario genérico y lo DESCARGUE en vez de intentar abrirlo en una pestaña.
//  3. Se crea un <a> invisible, se le asigna href + download, se ejecuta click()
//     de forma síncrona y luego se limpia el DOM y la URL temporal.

function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.style.display = 'none';
  link.href = url;
  link.download = filename;

  document.body.appendChild(link);
  link.click();

  // Limpieza de DOM de forma síncrona y de memoria poco después.
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 100);
}

// Fuerza la descarga de un PDF ya generado (Blob / ArrayBuffer) como binario
// genérico octet-stream. Debe llamarse SIN await previo para conservar el gesto.
export function downloadPdfBlob(blob: Blob, filename: string): void {
  triggerBlobDownload(new Blob([blob], { type: 'application/octet-stream' }), filename);
}

export function downloadTextFile(filename: string, content: string): void {
  triggerBlobDownload(new Blob([content], { type: 'application/octet-stream' }), filename);
}

// Crea un object URL (blob:) a partir de un texto SIN dispararlo. Se usa cuando
// la descarga programática puede ser bloqueada por el navegador (por ejemplo
// dentro de un iframe de vista previa): dejamos el enlace real disponible para
// que el usuario lo clickee directamente, que es la vía más confiable.
export function createTextBlobUrl(content: string, type = 'application/octet-stream'): string {
  return URL.createObjectURL(new Blob([content], { type }));
}

// Dispara la descarga forzada de un object URL ya creado. Crea un <a> invisible
// con el atributo `download` explícito para que el navegador GUARDE el archivo
// en lugar de intentar abrirlo en una pestaña nueva. La URL se conserva viva
// (solo se limpia el nodo del DOM) para que el enlace de respaldo manual siga
// funcionando; su liberación final la controla quien la creó.
export function triggerUrlDownload(url: string, filename: string): void {
  const link = document.createElement('a');
  link.style.display = 'none';
  link.href = url;
  link.download = filename;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  // Limpieza diferida del nodo para no cortar la descarga antes de que empiece.
  setTimeout(() => {
    if (link.parentNode) {
      link.parentNode.removeChild(link);
    }
  }, 100);
}

// Libera un object URL creado previamente con createTextBlobUrl.
export function revokeObjectUrl(url: string): void {
  try {
    URL.revokeObjectURL(url);
  } catch {
    /* ignore */
  }
}